
import * as THREE from "../../libs/three.js/build/three.module.js";
import {TextSprite} from "../TextSprite.js";

export class FeatureZone extends THREE.Object3D {

	constructor(polygon, minHeight, maxHeight, properties = {}) {
		super();

		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = "feature_zone_" + this.constructor.counter;

		// 2D polygon in scene coords: array of [x, y]
		this.polygon = polygon;
		this.minHeight = minHeight;
		this.maxHeight = maxHeight;
		this.properties = properties;
		this.lidarCentroid = null;

		// Datum anchoring: known reference elevation attached by user (Feature 2)
		this.datumAnchor = null;
		// If this landmark was derived from another (e.g., invert from rim)
		this.derivedFrom = null;

		// Compute centroid to use as the Object3D origin.
		// All geometry is built in local coords relative to this point
		// to avoid GPU float32 precision jitter with large projected coordinates.
		let centroid = this.computeCentroid(polygon);
		this.position.set(centroid[0], centroid[1], 0);

		// Build local-space polygon (relative to centroid)
		let localPolygon = polygon.map(([x, y]) => [x - centroid[0], y - centroid[1]]);

		let shape = new THREE.Shape();
		shape.moveTo(localPolygon[0][0], localPolygon[0][1]);
		for (let i = 1; i < localPolygon.length; i++) {
			shape.lineTo(localPolygon[i][0], localPolygon[i][1]);
		}
		shape.closePath();

		let depth = maxHeight - minHeight;
		let extrudeGeometry = new THREE.ExtrudeGeometry(shape, {
			depth: depth,
			bevelEnabled: false,
		});

		this.material = new THREE.MeshBasicMaterial({
			color: 0x0066ff,
			transparent: true,
			opacity: 0.3,
			depthTest: true,
			depthWrite: false,
			side: THREE.DoubleSide,
		});

		this.mesh = new THREE.Mesh(extrudeGeometry, this.material);
		this.mesh.position.z = minHeight;
		this.add(this.mesh);

		// Wireframe outline
		let edgesGeometry = new THREE.EdgesGeometry(extrudeGeometry);
		this.frame = new THREE.LineSegments(
			edgesGeometry,
			new THREE.LineBasicMaterial({ color: 0x000000 })
		);
		this.frame.position.z = minHeight;
		this.add(this.frame);

		// Label at centroid (which is local origin), at maxHeight
		let labelText = properties.label || properties.id || this.name;
		this.label = new TextSprite(labelText);
		this.label.setBorderColor({ r: 0, g: 255, b: 0, a: 0.0 });
		this.label.setBackgroundColor({ r: 0, g: 255, b: 0, a: 0.0 });
		this.label.material.depthTest = false;
		this.label.material.depthWrite = false;
		this.label.material.transparent = true;
		this.label.position.set(0, 0, maxHeight);
		this.add(this.label);

		// Required for InputHandler.getHoveredElements() recognition
		this.addEventListener('select', e => {});
		this.addEventListener('deselect', e => {});
	}

	computeCentroid(polygon) {
		let cx = 0;
		let cy = 0;
		for (let [x, y] of polygon) {
			cx += x;
			cy += y;
		}
		return [cx / polygon.length, cy / polygon.length];
	}

	// Delegates to mesh raycast, same pattern as BoxVolume.raycast()
	raycast(raycaster, intersects) {
		let is = [];
		this.mesh.raycast(raycaster, is);

		if (is.length > 0) {
			let I = is[0];
			intersects.push({
				distance: I.distance,
				object: this,
				point: I.point.clone(),
			});
		}
	}

	// Compute a world-space axis-aligned bounding box for this volume.
	getWorldBoundingBox() {
		let xs = this.polygon.map(p => p[0]);
		let ys = this.polygon.map(p => p[1]);
		return new THREE.Box3(
			new THREE.Vector3(Math.min(...xs), Math.min(...ys), this.minHeight),
			new THREE.Vector3(Math.max(...xs), Math.max(...ys), this.maxHeight),
		);
	}

	// Traverse the full octree and collect world-space positions of points
	// within this volume. Shared by computeLidarCentroid and computeSurfaceElevation.
	// callback(wx, wy, wz) is called for each matching point.
	// Returns true if all overlapping nodes were loaded, false if some are pending.
	_traversePointsInVolume(pointclouds, callback) {
		let zoneBox = this.getWorldBoundingBox();
		let hasPendingLoads = false;

		for (let pointcloud of pointclouds) {
			if (!pointcloud.initialized()) {
				continue;
			}

			let pcMatrix = pointcloud.matrixWorld;

			// Traverse the full octree hierarchy via DFS
			let stack = [pointcloud.root];

			while (stack.length > 0) {
				let node = stack.pop();
				if (!node) continue;

				// Test if node's world-space bounding box overlaps the zone
				let nodeBox = node.getBoundingBox();
				let nodeBoxWorld = new THREE.Box3(
					nodeBox.min.clone().applyMatrix4(pcMatrix),
					nodeBox.max.clone().applyMatrix4(pcMatrix),
				);

				if (!zoneBox.intersectsBox(nodeBoxWorld)) {
					continue;
				}

				// If this node is an unloaded geometry node, trigger loading
				if (node.isGeometryNode() && !node.isLoaded()) {
					node.load();
					hasPendingLoads = true;
					continue;
				}

				// Extract points from loaded nodes
				let geometry = node.isTreeNode()
					? node.geometryNode.geometry
					: node.geometry;

				if (geometry) {
					let positionAttribute = geometry.attributes.position;
					if (positionAttribute) {
						// Compute world transform for this node's vertices.
						// Vertex positions are relative to the node's bounding box min.
						// World pos = pcMatrix * (vertex + boundingBox.min)
						let nodeWorld;
						if (node.isTreeNode() && node.sceneNode) {
							nodeWorld = node.sceneNode.matrixWorld;
						} else {
							// For geometry nodes, build the transform manually
							let translate = new THREE.Matrix4().makeTranslation(
								nodeBox.min.x, nodeBox.min.y, nodeBox.min.z
							);
							nodeWorld = new THREE.Matrix4().multiplyMatrices(pcMatrix, translate);
						}

						let positions = positionAttribute.array;
						let numPoints = positionAttribute.count;
						let e = nodeWorld.elements;

						for (let i = 0; i < numPoints; i++) {
							let lx = positions[i * 3 + 0];
							let ly = positions[i * 3 + 1];
							let lz = positions[i * 3 + 2];

							let wx = e[0] * lx + e[4] * ly + e[8] * lz + e[12];
							let wy = e[1] * lx + e[5] * ly + e[9] * lz + e[13];
							let wz = e[2] * lx + e[6] * ly + e[10] * lz + e[14];

							if (this.containsPoint(wx, wy, wz)) {
								callback(wx, wy, wz);
							}
						}
					}
				}

				// Recurse into children
				let children = node.getChildren();
				for (let child of children) {
					stack.push(child);
				}
			}
		}

		return !hasPendingLoads;
	}

	// Compute the average position of LiDAR points within this volume.
	// Traverses the full octree hierarchy (not just visible nodes) so that
	// loaded nodes outside the camera frustum are still included.
	// Returns true if all overlapping nodes were loaded, false if loads are pending.
	computeLidarCentroid(pointclouds) {
		let sumX = 0, sumY = 0, sumZ = 0;
		let count = 0;

		let allLoaded = this._traversePointsInVolume(pointclouds, (wx, wy, wz) => {
			sumX += wx;
			sumY += wy;
			sumZ += wz;
			count++;
		});

		if (count > 0) {
			this.lidarCentroid = new THREE.Vector3(sumX / count, sumY / count, sumZ / count);
		} else {
			// Fallback: geometric centroid at mid-height
			let centroid = this.computeCentroid(this.polygon);
			let midZ = (this.minHeight + this.maxHeight) / 2;
			this.lidarCentroid = new THREE.Vector3(centroid[0], centroid[1], midZ);
		}

		return allLoaded;
	}

	// Compute the median surface elevation of LiDAR points within this volume.
	// Uses median rather than mean for robustness against outliers
	// (e.g., interior points in manholes).
	// Returns { elevation, pointCount } or null if no points found.
	computeSurfaceElevation(pointclouds) {
		let zValues = [];

		this._traversePointsInVolume(pointclouds, (wx, wy, wz) => {
			zValues.push(wz);
		});

		if (zValues.length === 0) {
			return null;
		}

		zValues.sort((a, b) => a - b);
		let mid = Math.floor(zValues.length / 2);
		let median = (zValues.length % 2 === 0)
			? (zValues[mid - 1] + zValues[mid]) / 2
			: zValues[mid];

		return { elevation: median, pointCount: zValues.length };
	}

	// 2D point-in-polygon (ray-casting) + height check
	containsPoint(x, y, z) {
		if (z < this.minHeight || z > this.maxHeight) {
			return false;
		}

		return this.pointInPolygon(x, y, this.polygon);
	}

	// Ray-casting point-in-polygon test (from ElevationZoneTool)
	pointInPolygon(px, py, polygon) {
		let inside = false;
		let n = polygon.length;

		for (let i = 0, j = n - 1; i < n; j = i++) {
			let xi = polygon[i][0];
			let yi = polygon[i][1];
			let xj = polygon[j][0];
			let yj = polygon[j][1];

			if (((yi > py) !== (yj > py)) &&
				(px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
				inside = !inside;
			}
		}

		return inside;
	}
}
